import { FunctionComponent, ReactElement, useEffect, useMemo } from 'react';
import { AxiosError } from 'axios';
import { Box } from '@mui/system';
import { Customer, SingleSite } from '@/core/entities';
import { ALL_SITES_ID, useCompletedCycles } from '@/queries/logs';
import { ApiError } from '@/shared/types/api-errors';
import useToastMessage from '@/shared/context/toast-message-context';
import DashboardItem from '../../DashboardItem';
import CompletedCyclesChart from './chart';
import { SelectedLocation } from './filters/LocationFiltersDialog';
import { QueryDateRange } from '../../types';

const BASE_TITLE = 'Completed Cycles';

interface CompletedCyclesProps {
	site?: SingleSite;
	locationsFilter: SelectedLocation[];
	locationId?: string;
	customer?: Pick<Customer, 'id' | 'name'>;
	onExport?: () => void;
	exportType: string | null;
	showAllSitesData: boolean;
	isAdmin: boolean;
	dateRange: QueryDateRange;
	onChartReady?: (ready: boolean) => void;
}

const CompletedCycles: FunctionComponent<CompletedCyclesProps> = ({
	site,
	locationId,
	showAllSitesData,
	customer,
	exportType,
	dateRange,
	onChartReady,
}): ReactElement => {
	const { dispatch } = useToastMessage();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const params: Record<string, any> = {
		fromDate: dateRange.start,
		toDate: dateRange.end,
	};

	if (customer) {
		params.customerId = customer.id;
	}

	if (site) {
		params.siteId = site.id;
	}

	if (locationId) {
		params.locationId = locationId;
	}
	const {
		data: completedCycles,
		error,
		isFetching: isFetchingCycles,
	} = useCompletedCycles(params, showAllSitesData ? ALL_SITES_ID : site?.id);

	const isLoading = useMemo(() => isFetchingCycles, [isFetchingCycles]);

	/* Handle errors */
	useEffect(() => {
		const { message, statusCode } = (error as AxiosError<ApiError>)?.response?.data || {};
		if (statusCode === 400) {
			dispatch({
				type: 'error',
				content: message,
			});
		}
	}, [error, dispatch]);

	const title = useMemo(
		() => (
			<>
				<Box component="span" sx={{ displayPrint: 'none' }}>
					{BASE_TITLE}
				</Box>
				<Box component="span" sx={{ display: 'none', displayPrint: 'inline' }}>
					{BASE_TITLE}
					{customer && ` - ${customer?.name}`}
					{site?.name && ` - ${site.name}`}
				</Box>
			</>
		),
		[site, customer]
	);

	return (
		<DashboardItem title={title}>
			<CompletedCyclesChart
				completedCycles={completedCycles}
				dateRange={dateRange}
				exportType={exportType}
				onChartReady={onChartReady}
				isLoading={isLoading}
			/>
		</DashboardItem>
	);
};

export default CompletedCycles;
